/**
 * ETF & Premium Panel
 * Global panel: BTC + ETH ETF net flows, Coinbase premium per asset
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassETFFlows, useCoinglassCBPremium } from '@/hooks/use-coinglass';
import { formatCompact, formatPercent } from '@/lib/utils';

export function ETFPremiumPanel() {
  const { data: etfData, isLoading: etfLoading } = useCoinglassETFFlows();
  const { data: premiumData, isLoading: premiumLoading } = useCoinglassCBPremium();

  if (etfLoading && premiumLoading) return <LoadingSkeleton className="h-48" lines={4} />;

  const etf = etfData?.etfFlows;
  const premiums = premiumData?.coinbasePremium;

  const hasEtf = etf?.btc || etf?.eth;
  const hasPremium = premiums && Object.keys(premiums).length > 0;

  if (!hasEtf && !hasPremium) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">ETF & Premium</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No data</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">ETF & Premium</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ETF Flows */}
        {hasEtf && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">ETF Net Flows</p>
            <div className="flex gap-4 text-xs">
              {etf?.btc && (
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">BTC</Badge>
                  <span className={etf.btc.netFlow > 0 ? 'text-green-500' : 'text-red-500'}>
                    {etf.btc.netFlow > 0 ? '+' : ''}${formatCompact(etf.btc.netFlow)}
                  </span>
                </div>
              )}
              {etf?.eth && (
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">ETH</Badge>
                  <span className={etf.eth.netFlow > 0 ? 'text-green-500' : 'text-red-500'}>
                    {etf.eth.netFlow > 0 ? '+' : ''}${formatCompact(etf.eth.netFlow)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Coinbase Premium */}
        {hasPremium && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Coinbase Premium</p>
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              {Object.entries(premiums).map(([asset, entry]) => (
                <div key={asset} className="flex items-center gap-1">
                  <span className="text-muted-foreground">{asset}:</span>
                  <span className={entry.premium > 0 ? 'text-green-500' : 'text-red-500'}>
                    {formatPercent(entry.premium * 100, 3)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
